import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, CalendarDays, Users, FolderOpen, DollarSign, FileAudio } from "lucide-react";
import api from "../lib/api";
import { FadeUp } from "../components/Reveal";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const pending = bookings.filter((b) => b.status === "pending").slice(0, 5);

  const cards = stats
    ? [
        { icon: ClipboardList, label: "Pending requests", value: stats.pending_requests, gold: stats.pending_requests > 0 },
        { icon: CalendarDays, label: "Confirmed upcoming", value: stats.upcoming_sessions },
        { icon: Users, label: "Clients", value: stats.total_clients },
        { icon: FolderOpen, label: "Active projects", value: stats.active_projects },
        { icon: DollarSign, label: "Est. revenue (month)", value: `$${stats.monthly_revenue.toLocaleString()}` },
        { icon: FileAudio, label: "Files delivered", value: stats.files_delivered },
      ]
    : [];

  return (
    <div data-testid="admin-overview">
      <FadeUp>
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-700">Studio Control</p>
        <h1 className="font-serif text-3xl sm:text-4xl mt-3 text-obsidian">Good to see you, <span className="italic gold-gradient-text">Empress</span></h1>
      </FadeUp>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-10">
        {cards.map((c, i) => (
          <FadeUp key={c.label} delay={i * 0.06} className="bg-white rounded-2xl border border-purple-100 p-6" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <c.icon size={18} className={c.gold ? "text-gold" : "text-purple-700"} />
            <p className="font-serif text-3xl mt-4 text-obsidian">{c.value}</p>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-1">{c.label}</p>
          </FadeUp>
        ))}
      </div>

      <FadeUp delay={0.2} className="mt-10 bg-white rounded-3xl border border-purple-100 p-7" data-testid="pending-requests-panel">
        <div className="flex items-center justify-between">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400">Awaiting your approval</p>
          <Link to="/admin/bookings" className="text-xs text-purple-700 hover:text-gold transition-colors" data-testid="goto-bookings">All bookings</Link>
        </div>
        <div className="mt-5 space-y-3">
          {pending.map((b) => (
            <div key={b.booking_id} className="flex flex-wrap items-center gap-4 p-4 rounded-2xl bg-lilac-mist" data-testid={`pending-${b.booking_id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium text-obsidian">{b.client_name} — {b.service_name}</p>
                <p className="text-xs text-slate-500 mt-1">{b.date} at {b.time} · {b.location_name} · ${b.price}</p>
              </div>
              <Link to="/admin/bookings" data-testid={`review-${b.booking_id}`}
                className="text-xs px-4 py-2 rounded-full bg-obsidian text-white hover:text-gold transition-colors">
                Review
              </Link>
            </div>
          ))}
          {pending.length === 0 && <p className="text-sm text-slate-400 italic">Inbox zero. Nothing waiting on you.</p>}
        </div>
      </FadeUp>
    </div>
  );
}
